import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, FileText, Scale, CheckCircle2, AlertTriangle, ShieldAlert, Wallet } from "lucide-react";
import { usePlatform } from "@/context/PlatformContext";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import PublicHeader from "@/components/PublicHeader";

export default function TermsAndConditions() {
  const navigate = useNavigate();
  const { settings } = usePlatform();
  const appTitle = settings?.title || "GEMS";

  return (
    <div className="min-h-screen bg-[#fdfdfd] text-slate-900 flex flex-col justify-between">
      <SEO
        title={`Terms & Conditions — ${appTitle}`}
        description={`Read the Terms & Conditions and User Agreement for using ${appTitle} group expense tracking platform.`}
        keywords="terms and conditions, user agreement, terms of service, acceptable use policy, group expense rules"
        canonicalUrl="https://splitly.app/terms"
        schemaType="WebPage"
      />
      {/* Header */}
      <PublicHeader showBack={true} />

      {/* Main Container */}
      <main className="max-w-4xl mx-auto px-6 lg:px-8 py-12 flex-1 w-full">
        {/* Banner */}
        <div className="mb-10 text-center sm:text-left">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-50 border border-blue-200/80 text-xs font-semibold text-blue-700 mb-4">
            <FileText className="w-4 h-4 text-blue-600" />
            User Agreement
          </div>
          <h1 className="heading text-4xl lg:text-5xl font-bold tracking-tight text-slate-900">
            Terms & Conditions
          </h1>
          <p className="mt-3 text-slate-500 text-sm">
            Last updated: August 27, 2026 • Effective immediately
          </p>
        </div>

        {/* Policy Body */}
        <div className="bg-white rounded-3xl p-8 sm:p-12 border border-slate-200/70 card-soft space-y-8 text-slate-700 text-sm leading-relaxed">
          <section>
            <div className="flex items-center gap-3 text-slate-900 font-semibold text-lg mb-3">
              <div className="w-8 h-8 rounded-xl bg-blue-100 text-blue-700 grid place-items-center">
                <Scale className="w-4 h-4" />
              </div>
              1. Acceptance of Terms
            </div>
            <p>
              By accessing, browsing, or using the <strong>{appTitle}</strong> application, website, or services, you agree to be bound by these Terms & Conditions. If you do not agree with any portion of these terms, you must discontinue using our services immediately.
            </p>
          </section>

          <hr className="border-slate-100" />

          <section>
            <div className="flex items-center gap-3 text-slate-900 font-semibold text-lg mb-3">
              <div className="w-8 h-8 rounded-xl bg-emerald-100 text-emerald-700 grid place-items-center">
                <CheckCircle2 className="w-4 h-4" />
              </div>
              2. User Accounts & Group Responsibilities
            </div>
            <p className="mb-3">To utilize features such as creating groups or tracking expenses, you must register for an account:</p>
            <ul className="list-disc pl-5 space-y-2 text-slate-600">
              <li>You are responsible for maintaining the confidentiality of your login credentials.</li>
              <li>Group admins are responsible for adding authorized members and setting custom group settings.</li>
              <li>You agree to input accurate expense information when recording split transactions.</li>
            </ul>
          </section>

          <hr className="border-slate-100" />

          <section>
            <div className="flex items-center gap-3 text-slate-900 font-semibold text-lg mb-3">
              <div className="w-8 h-8 rounded-xl bg-amber-100 text-amber-700 grid place-items-center">
                <AlertTriangle className="w-4 h-4" />
              </div>
              3. Acceptable Use Policy
            </div>
            <p className="mb-3">Users agree not to use {appTitle} for any illegal or unauthorized activities, including:</p>
            <ul className="list-disc pl-5 space-y-2 text-slate-600">
              <li>Submitting fraudulent financial entries or impersonating another user or entity.</li>
              <li>Attempting to compromise application security or reverse-engineer API endpoints.</li>
              <li>Using automated scraping tools or uploading malicious code to our servers.</li>
            </ul>
          </section>

          <hr className="border-slate-100" />

          <section>
            <div className="flex items-center gap-3 text-slate-900 font-semibold text-lg mb-3">
              <div className="w-8 h-8 rounded-xl bg-rose-100 text-rose-700 grid place-items-center">
                <ShieldAlert className="w-4 h-4" />
              </div>
              4. Financial Ledger Disclaimer
            </div>
            <p>
              {appTitle} serves as a software calculations ledger and management tool for tracking expense allocations among members. {appTitle} is not a bank or registered financial institution. Users remain responsible for completing actual peer-to-peer monetary transfers.
            </p>
          </section>

          <hr className="border-slate-100" />

          <section>
            <div className="flex items-center gap-3 text-slate-900 font-semibold text-lg mb-3">
              <div className="w-8 h-8 rounded-xl bg-purple-100 text-purple-700 grid place-items-center">
                <FileText className="w-4 h-4" />
              </div>
              5. Contact & Support
            </div>
            <p>
              For questions regarding these Terms & Conditions or general inquiries, please contact us at{" "}
              <a
                href="https://saharey.com/contactus"
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-900 font-semibold underline underline-offset-2 hover:text-blue-600"
              >
                https://saharey.com/contactus
              </a>.
            </p>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}
